import { parseAppDate } from '@/utils/date';

import { CategoryAmount } from '@/interfaces/cashFlow';
import {
  CashFlowChartResponse,
  CashFlowChartSeries,
} from '@/store/transactionsApi';
// TODO: Revisit change fields. The chart-derived cash flow summary always
// returns zero change values, and ChartCurrencyTitle currently does not render
// comparison data. Remove these fields or restore real period-over-period
// calculations if comparison UI is still required.
const ZERO_CHANGE = { amount: 0, percentage: 0 };
const AVERAGE_DAYS_PER_MONTH = 365 / 12;

export interface CashFlowSummaryViewModel {
  netCashFlowData: {
    realIncome: number;
    income: number;
    spending: number;
    change: {
      amount: number;
      percentage: number;
    };
  };
  averageSpendingData: {
    amount: number;
    change: {
      amount: number;
      percentage: number;
    };
    topSpendingCategory: string;
  };
  incomeByCategory: CategoryAmount[];
  spendingByCategory: CategoryAmount[];
  topAmount: number;
}

function findSeries(
  chartData: CashFlowChartResponse | undefined,
  name: string,
): CashFlowChartSeries | undefined {
  const target = name.toLowerCase();

  return chartData?.Records.find(
    (series) =>
      series.id.toLowerCase() === target ||
      series.name.toLowerCase() === target,
  );
}

function isSpendingSeries(series: CashFlowChartSeries) {
  return (
    series.id.toLowerCase() === 'spending' ||
    series.name.toLowerCase() === 'spending'
  );
}

function findSpendingSeries(
  chartData: CashFlowChartResponse | undefined,
): CashFlowChartSeries | undefined {
  return chartData?.Records.find(isSpendingSeries);
}

function sumSeries(series: CashFlowChartSeries | undefined) {
  return series?.data.reduce((sum, point) => sum + point.y, 0) ?? 0;
}

function sumAllSeries(chartData: CashFlowChartResponse | undefined) {
  return (
    chartData?.Records.reduce(
      (total, series) =>
        total + series.data.reduce((sum, point) => sum + point.y, 0),
      0,
    ) ?? 0
  );
}

function sumAbsoluteSeries(series: CashFlowChartSeries | undefined) {
  return series?.data.reduce((sum, point) => sum + Math.abs(point.y), 0) ?? 0;
}

function mapSeriesCategoryAmounts(
  seriesList: CashFlowChartSeries[],
): CategoryAmount[] {
  return seriesList
    .flatMap((series) => series.data)
    .map((point) => ({
      category: point.name || 'Uncategorized',
      amount: Math.abs(point.y),
    }))
    .sort((a, b) => b.amount - a.amount);
}

function getDateRangeDayCount(startDate: string, endDate: string) {
  const start = parseAppDate(startDate);
  const end = parseAppDate(endDate);

  if (!start || !end) {
    return 1;
  }

  return Math.max(end.diff(start, 'day') + 1, 1);
}

export function buildCashFlowSummaryFromChart(
  chartData: CashFlowChartResponse | undefined,
  startDate: string,
  endDate: string,
): CashFlowSummaryViewModel {
  const incomeSeries = findSeries(chartData, 'income');
  const spendingSeries = findSpendingSeries(chartData);
  const totalIncome = sumSeries(incomeSeries);
  const displayedSpending = sumAbsoluteSeries(spendingSeries);
  const netCashFlow = sumAllSeries(chartData);
  const incomeByCategory = mapSeriesCategoryAmounts(
    incomeSeries ? [incomeSeries] : [],
  );
  const spendingByCategory = mapSeriesCategoryAmounts(
    spendingSeries ? [spendingSeries] : [],
  );
  const topAmount = Math.max(
    incomeByCategory[0]?.amount || 0,
    spendingByCategory[0]?.amount || 0,
  );
  const topSpendingCategory =
    spendingByCategory.length > 0 ? spendingByCategory[0].category : 'N/A';
  const dayCount = getDateRangeDayCount(startDate, endDate);
  const netCashFlowData = {
    realIncome: netCashFlow,
    income: totalIncome,
    spending: displayedSpending,
    change: ZERO_CHANGE,
  };

  return {
    netCashFlowData,
    averageSpendingData: {
      amount: (netCashFlowData.spending / dayCount) * AVERAGE_DAYS_PER_MONTH,
      change: ZERO_CHANGE,
      topSpendingCategory,
    },
    incomeByCategory,
    spendingByCategory,
    topAmount,
  };
}
